import { FormEvent, useEffect, useMemo, useState } from "react";
import { fetchSlotBusyVehicles, type SlotBusyVehicle } from "../../api/appointments";
import { ApiError, api } from "../../api/client";
import { useDirectoryBundle } from "../../api/directory";
import { useTrainingCohortTree } from "../../api/trainingCohorts";
import { UI_LOADING } from "../../constants/uiLabels";
import {
  CohortFilterBar,
  type CohortFilterValue,
} from "../cohorts/CohortFilterBar";
import { StudentFormModal } from "../students/StudentFormModal";
import { formatVehicleOptionLabel, type AppointmentEditContext } from "./WeeklyCalendar";
import "./NewAppointmentModal.css";

export type NewAppointmentSlotContext = {
  date: string;
  startHHmm: string;
  endHHmm: string;
  preferredInstructorId?: string;
  /** Takvimdeki izinli slotta onaylanmis istisna rezervasyon akisi */
  leaveException?: boolean;
};

type LessonType = "normal" | "special" | "extra";

type SuggestedSlot = {
  starts_at: string;
  ends_at: string;
  distance_minutes: number;
  suggestion_token?: string;
};

type PlanResponse = {
  created?: boolean;
  updated?: boolean;
  mode?: "strict" | "override";
  appointment?: { id: string };
  requires_override?: boolean;
  conflicts?: unknown;
  suggested_slots?: SuggestedSlot[];
  message?: string;
};

type Props = {
  slot: NewAppointmentSlotContext;
  edit?: AppointmentEditContext;
  onClose: () => void;
  onCreated: () => void;
};

function buildIsoDateTime(date: string, hhmm: string): string {
  return `${date}T${hhmm}:00+03:00`;
}

function formatSuggestion(slot: SuggestedSlot): string {
  const start = new Date(slot.starts_at);
  const end = new Date(slot.ends_at);
  const fmt = (d: Date) =>
    d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
  return `${fmt(start)} - ${fmt(end)} (${slot.distance_minutes} dk uzakta)`;
}

function normalizeLessonType(value: string): LessonType {
  if (value === "special" || value === "extra") return value;
  return "normal";
}

const EMPTY_COHORT: CohortFilterValue = { periodId: "", groupId: "", subgroupId: "" };

export function NewAppointmentModal({ slot, edit, onClose, onCreated }: Props) {
  const isEdit = Boolean(edit);
  const [cohortFilter, setCohortFilter] = useState<CohortFilterValue>(EMPTY_COHORT);
  const directory = useDirectoryBundle(undefined, {
    trainingPeriodId: cohortFilter.periodId || undefined,
    trainingGroupId: cohortFilter.groupId || undefined,
    trainingSubgroupId: cohortFilter.subgroupId || undefined,
  });
  const cohortTree = useTrainingCohortTree();
  const [creatingStudent, setCreatingStudent] = useState(false);
  const [studentId, setStudentId] = useState(edit?.studentId || "");
  const [instructorId, setInstructorId] = useState(edit?.instructorId || "");
  const [vehicleId, setVehicleId] = useState(edit?.vehicleId || "");
  const [lessonType, setLessonType] = useState<LessonType>(edit ? normalizeLessonType(edit.lessonType) : "normal");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggested, setSuggested] = useState<SuggestedSlot[]>([]);
  const [conflictMode, setConflictMode] = useState(false);
  const [conflictReason, setConflictReason] = useState("");
  const [busyVehicles, setBusyVehicles] = useState<Map<string, SlotBusyVehicle>>(() => new Map());

  const activeSlot = edit || slot;
  const startsAt = useMemo(
    () => buildIsoDateTime(activeSlot.date, activeSlot.startHHmm),
    [activeSlot.date, activeSlot.startHHmm],
  );
  const endsAt = useMemo(
    () => buildIsoDateTime(activeSlot.date, activeSlot.endHHmm),
    [activeSlot.date, activeSlot.endHHmm],
  );

  const resolvedBranchId = useMemo(() => {
    if (!directory.data) return undefined;
    const instructor = directory.data.instructors.find((i) => i.id === instructorId);
    if (instructor?.branch_id) return instructor.branch_id;
    const student = directory.data.students.find((s) => s.id === studentId);
    return student?.branch_id;
  }, [directory.data, instructorId, studentId]);

  useEffect(() => {
    let cancelled = false;
    const loadBusy = async () => {
      try {
        const res = await fetchSlotBusyVehicles({
          startsAt,
          endsAt,
          excludeAppointmentId: edit?.appointmentId,
        });
        if (cancelled) return;
        setBusyVehicles(new Map(res.busy_vehicles.map((row) => [row.vehicle_id, row])));
      } catch {
        if (!cancelled) setBusyVehicles(new Map());
      }
    };
    void loadBusy();
    return () => {
      cancelled = true;
    };
  }, [startsAt, endsAt, edit?.appointmentId]);

  const pickFirstFreeVehicleId = (vehicles: { id: string }[], busy: Map<string, SlotBusyVehicle>) =>
    vehicles.find((v) => !busy.has(v.id))?.id || "";

  const vehicleBusySuffix = (id: string) => {
    const busy = busyVehicles.get(id);
    if (!busy) return "";
    return ` — Dolu (${busy.label})`;
  };

  useEffect(() => {
    if (edit) {
      setStudentId(edit.studentId);
      setInstructorId(edit.instructorId);
      setVehicleId(edit.vehicleId);
      setLessonType(normalizeLessonType(edit.lessonType));
      return;
    }
    const data = directory.data;
    if (!data) return;
    setStudentId((s) => {
      if (s && data.students.some((row) => row.id === s)) return s;
      return data.students[0]?.id || "";
    });
    if (slot.preferredInstructorId && data.instructors.some((i) => i.id === slot.preferredInstructorId)) {
      setInstructorId(slot.preferredInstructorId);
    } else {
      setInstructorId((ins) => ins || data.instructors[0]?.id || "");
    }
  }, [directory.data, edit, slot.date, slot.startHHmm, slot.endHHmm, slot.preferredInstructorId]);

  useEffect(() => {
    const data = directory.data;
    if (!data || edit) return;
    setVehicleId((v) => {
      if (v && !busyVehicles.has(v)) return v;
      return pickFirstFreeVehicleId(data.vehicles, busyVehicles);
    });
  }, [directory.data, edit, busyVehicles, slot.date, slot.startHHmm, slot.endHHmm]);

  useEffect(() => {
    if (isEdit || !directory.data) return;
    if (studentId && !directory.data.students.some((s) => s.id === studentId)) {
      setStudentId(directory.data.students[0]?.id || "");
    }
  }, [cohortFilter, directory.data, isEdit, studentId]);

  const submit = async (event: FormEvent, override: boolean) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const leaveException = Boolean(!isEdit && slot.leaveException);
      const leaveOnlyOverride =
        leaveException &&
        !override &&
        !conflictMode;

      const doPlan = async (withOverride: boolean, reason: string) => {
        const payload: Record<string, unknown> = {
          studentId,
          instructorId,
          vehicleId,
          startsAt,
          endsAt,
          lessonType,
        };
        if (resolvedBranchId) {
          payload.branchId = resolvedBranchId;
        }
        if (withOverride) {
          payload.conflictOverride = true;
          payload.conflictReason = reason;
        }
        if (notes.trim()) {
          payload.notes = notes.trim();
        }
        return isEdit
          ? await api.post<PlanResponse>(`/appointments/${edit!.appointmentId}/replan`, payload)
          : await api.post<PlanResponse>("/appointments/plan", payload);
      };

      const defaultReason = conflictReason.trim() || "Kullanıcı cakisma istisnasi ile devam etti";
      let response = await doPlan(override, defaultReason);

      const conflicts = Array.isArray(response.conflicts) ? response.conflicts : [];
      const leaveOnly =
        conflicts.length > 0 &&
        conflicts.every((c) => {
          const row = c as { conflict_type?: string; conflict_reason?: string };
          const type = String(row.conflict_type || "");
          const reason = String(row.conflict_reason || "");
          return (
            type === "instructor_leave" ||
            reason === "instructor_on_leave" ||
            reason === "instructor_weekly_leave"
          );
        });

      // İzinli slottan gelindiyse ve tek çakışma izin ise ikinci kez gerekçe sormadan istisna uygula.
      if (response.requires_override && leaveOnlyOverride && leaveOnly) {
        response = await doPlan(true, "İzinli saatte istisna randevu");
      }

      if (response.created || response.updated) {
        onCreated();
        return;
      }

      if (response.requires_override) {
        setConflictMode(true);
        setSuggested(response.suggested_slots || []);
        setError(
          response.message ||
            "Bu zaman diliminde cakisma var. Lutfen bir oneriyi Seçin veya istisna ile devam edin.",
        );
        return;
      }

      setError(response.message || (isEdit ? "Randevu Güncellenemedi." : "Randevu olusturulamadi."));
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || (isEdit ? "Randevu Güncellenemedi." : "Randevu olusturulamadi."));
      } else {
        setError("Sunucuya ulasilamadi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const directoryReady = directory.data && !directory.loading;
  const submitDisabled =
    submitting || !studentId || !instructorId || !vehicleId || !directoryReady;

  return (
    <>
    <div className="kp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-modal kp-card">
        <div className="kp-modal__head">
          <div>
            <strong>{isEdit ? "Randevuyu Düzenle" : "Yeni Randevu"}</strong>
            <span>
              {activeSlot.date} {activeSlot.startHHmm} - {activeSlot.endHHmm}
            </span>
          </div>
          <div className="kp-modal__head-actions">
            <button
              type="button"
              className="kp-btn kp-btn--primary"
              onClick={() => setCreatingStudent(true)}
              disabled={submitting || creatingStudent}
            >
              + Yeni Kursiyer
            </button>
            <button className="kp-btn kp-btn--ghost" onClick={onClose} disabled={submitting}>
              Kapat
            </button>
          </div>
        </div>

        {directory.loading && <div className="kp-loading">Liste {UI_LOADING}</div>}
        {directory.error && <div className="kp-error">{directory.error}</div>}
        {!isEdit && slot.leaveException && (
          <div className="kp-error" style={{ background: "#fffbeb", borderColor: "#fcd34d", color: "#92400e" }}>
            Bu saat eğitmen izinli görünüyor. Kayıt, izin istisnası olarak oluşturulacak; diğer izinli
            saatler aynı kalır.
          </div>
        )}

        {directoryReady && (
          <form
            className="kp-modal__form"
            onSubmit={(e) => submit(e, conflictMode)}
            autoComplete="off"
          >
            {!isEdit && (
              <div className="kp-modal__field kp-modal__field--full">
                <span>Kayit donemi / Grup / Alt grup</span>
                <CohortFilterBar value={cohortFilter} onChange={setCohortFilter} />
              </div>
            )}

            <label className="kp-modal__field">
              <span>Kursiyer</span>
              <select
                className="kp-input"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                required
              >
                <option value="">Seciniz...</option>
                {directory.data!.students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.full_name}
                  </option>
                ))}
              </select>
            </label>

            <label className="kp-modal__field">
              <span>Eğitmen</span>
              <select
                className="kp-input"
                value={instructorId}
                onChange={(e) => setInstructorId(e.target.value)}
                required
              >
                <option value="">Seciniz...</option>
                {directory.data!.instructors.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.full_name}
                    {i.employment_type === "daily_paid" ? " (gunluk)" : ""}
                  </option>
                ))}
              </select>
            </label>

            <label className="kp-modal__field">
              <span>Arac</span>
              <select
                className="kp-input"
                value={vehicleId}
                onChange={(e) => setVehicleId(e.target.value)}
                required
              >
                <option value="">Seciniz...</option>
                {directory.data!.vehicles.map((v) => (
                  <option key={v.id} value={v.id} disabled={busyVehicles.has(v.id)}>
                    {formatVehicleOptionLabel(v.plate_no, v.brand_model, v.gear_type)}
                    {vehicleBusySuffix(v.id)}
                  </option>
                ))}
              </select>
            </label>

            <label className="kp-modal__field">
              <span>Ders Tipi</span>
              <select
                className={`kp-input kp-modal__lesson-select kp-modal__lesson-select--${lessonType}`}
                value={lessonType}
                onChange={(e) => setLessonType(e.target.value as LessonType)}
              >
                <option value="normal">Normal Ders</option>
                <option value="extra">Ek Ders</option>
                <option value="special">Özel Ders</option>
              </select>
            </label>

            {!isEdit && (
              <label className="kp-modal__field kp-modal__field--full">
                <span>Notlar (opsiyonel)</span>
                <textarea
                  className="kp-input"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Bu randevu icin ek notlar"
                />
              </label>
            )}

            {error && <div className="kp-modal__error">{error}</div>}

            {conflictMode && suggested.length > 0 && (
              <div className="kp-modal__suggestions">
                <strong>Onerilen yakin ders saatleri</strong>
                <ul>
                  {suggested.map((s) => (
                    <li key={`${s.starts_at}-${s.ends_at}`}>{formatSuggestion(s)}</li>
                  ))}
                </ul>
                <small>Boyle bir oneriye gecmek icin formu kapatip ilgili ders saatinden yeniden acabilirsiniz.</small>
              </div>
            )}

            {conflictMode && (
              <label className="kp-modal__field kp-modal__field--full">
                <span>Istisna gerekcesi</span>
                <input
                  className="kp-input"
                  type="text"
                  value={conflictReason}
                  onChange={(e) => setConflictReason(e.target.value)}
                  placeholder="Neden cakismaya ragmen kaydediliyor?"
                />
              </label>
            )}

            <div className="kp-modal__actions">
              <button type="button" className="kp-btn" onClick={onClose} disabled={submitting}>
                İptal
              </button>
              {conflictMode ? (
                <>
                  <button
                    type="button"
                    className="kp-btn"
                    onClick={(e) => submit(e as unknown as FormEvent, false)}
                    disabled={submitting}
                  >
                    Yeniden Dene (kurallara uygun)
                  </button>
                  <button
                    type="submit"
                    className="kp-btn kp-btn--danger"
                    disabled={submitDisabled}
                  >
                    {submitting ? "Kaydediliyor…" : isEdit ? "Istisna ile Güncelle" : "Istisna ile olustur"}
                  </button>
                </>
              ) : (
                <button type="submit" className="kp-btn kp-btn--primary" disabled={submitDisabled}>
                  {submitting
                    ? "Kaydediliyor…"
                    : isEdit
                      ? "Randevuyu Kaydet"
                      : "Randevu Oluştur"}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>

    {creatingStudent && (
      <StudentFormModal
        student={null}
        cohortTree={cohortTree.tree}
        onClose={() => setCreatingStudent(false)}
        onSaved={(student) => {
          const cohort = student.training_cohort;
          setCohortFilter({
            periodId: cohort?.period_id || "",
            groupId: cohort?.group_id || "",
            subgroupId: student.training_subgroup_id || "",
          });
          setStudentId(student.id);
          setCreatingStudent(false);
          directory.reload();
        }}
      />
    )}
    </>
  );
}
